import React, { createContext, useContext, useEffect, useState } from 'react'
import { supabase, getProfile } from '../lib/supabase'
import { canWriteFinance, canApprove, DEFAULT_DASHBOARD } from '../lib/navConfig'

const AuthContext = createContext({})

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null)
      if (session?.user) loadProfile(session.user.id)
      else setLoading(false)
    })

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
      setUser(session?.user ?? null)
      if (session?.user) loadProfile(session.user.id)
      else { setProfile(null); setLoading(false) }
    })

    return () => subscription.unsubscribe()
  }, [])

  const loadProfile = async (userId) => {
    const { data } = await getProfile(userId)
    setProfile(data)
    setLoading(false)
  }

  // Existing permission checks — preserved so nothing breaks
  const isAdmin = profile?.permission_level === 'admin'
  const isAdminOrManager = ['admin', 'manager'].includes(profile?.permission_level)
  const canEditData = profile?.permission_level !== 'view_only'

  // New role-based system
  const userRole = profile?.user_role || 'staff'
  const isFinanceAdmin = userRole === 'finance_admin'
  const isOpsAdmin = userRole === 'ops_admin'
  const isStaff = userRole === 'staff'

  // Finance write permission — only finance_admin can post journals, record equity etc.
  const hasFinanceWrite = canWriteFinance(userRole)

  // Approval permission — both admins can approve
  const hasApprovalRights = canApprove(userRole)

  // Default dashboard for this user
  const defaultDashboard = DEFAULT_DASHBOARD[userRole] || '/dashboard/ops'

  return (
    <AuthContext.Provider value={{
      user, profile, loading,
      // Legacy — preserved
      isAdmin, isAdminOrManager, canEditData,
      // New role system
      userRole, isFinanceAdmin, isOpsAdmin, isStaff,
      hasFinanceWrite, hasApprovalRights, defaultDashboard,
      refreshProfile: () => loadProfile(user?.id),
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)
